import { getTOtp, syncConnections } from "@/api/rest/authenticated";
import Loading from "@/components/Loading";
import { AuthSignUpPageContainer } from "@/components/pages/Auth/AuthSignUpPageOutlet";
import AuthSolidButton from "@/components/pages/Auth/components/AuthSolidButton";
import { useAppSelector } from "@/redux/hooks";
import { Retailer } from "@/redux/types";
import CryptoJS from "crypto-js";
import { isNil } from "lodash";
import md5 from "md5";
import { useEffect, useState } from "react";
import { CheckoutInputV2 } from "../../../CheckoutInputV2";
import { AccountConnectBenefits } from "../../../CheckoutSummary/components/AccountRewards";
import { useCheckoutUI } from "../../CheckoutUIProvider";
import { useCheckoutRetailer } from "../../hooks/useCheckoutRetailer";

const ROW_SPACING = 25;

export const useInjectRetailerCredentials = () => {
  const [state] = useCheckoutUI();

  const retailers = useAppSelector((state) => state.retailers);
  const currentRetailer = retailers.find(
    (x) => x.retailerId === state.context.checkoutIntent?.retailerId
  );
  const [username, setUsername] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { token, currentStamp } = await getTOtp();
        const accountsDataEncrypted = await syncConnections({ currentStamp });
        const accounts = JSON.parse(
          CryptoJS.AES.decrypt(accountsDataEncrypted, token).toString(
            CryptoJS.enc.Utf8
          )
        );

        const account = accounts?.find(
          (x: any) => x.retailerId === currentRetailer?.parentRetailerId
        );

        if (account && currentRetailer?.retailerId) {
          setUsername(account.credential.username);
          setPassword(account.credential.password);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [currentRetailer]);

  return { username, password, loading };
};

export const CheckoutConnectRetailer = ({
  onSaveCredentials,
  submitting = false,
  usernameFromCheckoutIntent,
  passwordHashFromCheckoutIntent,
}: {
  onSaveCredentials: (username: string, password: string) => Promise<void>;
  submitting?: boolean;
  usernameFromCheckoutIntent?: string;
  passwordHashFromCheckoutIntent?: string;
}) => {
  const {
    username: usernameFromState,
    password: decryptedPassword,
    loading,
  } = useInjectRetailerCredentials();

  const [state, send] = useCheckoutUI();
  const { checkoutIntent } = state.context;

  const [username, setUsername] = useState<string>(
    usernameFromCheckoutIntent ?? usernameFromState ?? ""
  );
  const [password, setPassword] = useState<string | null>(null);

  useEffect(() => {
    // Set the default password to the decrypted password if it matches the hash
    if (!loading) {
      const credentialsMatch =
        username === usernameFromCheckoutIntent &&
        md5(password ?? "") === passwordHashFromCheckoutIntent;

      if (!credentialsMatch) {
        setPassword(decryptedPassword);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decryptedPassword]);

  const edited =
    !loading &&
    (username !== usernameFromCheckoutIntent ||
      md5(password ?? "") !== passwordHashFromCheckoutIntent);

  useEffect(() => {
    if (!loading && !isNil(password)) {
      // if the password is set from auto or user inpput
      if (edited) {
        send({
          type: "CHANGE_RETAILER_LOGIN_COMPLETE_STATUS",
          data: { forceComplete: true },
        });
      } else {
        send({
          type: "CHANGE_RETAILER_LOGIN_COMPLETE_STATUS",
          data: { forceComplete: false },
        });
      }
    }
  }, [edited, loading, password, send]);

  const getUserNameFieldHeading = (retailer: Retailer) => {
    if (retailer.retailerLoginUsernameFieldTypes?.length) {
      const supportedFieldTypes = retailer.retailerLoginUsernameFieldTypes;
      if (
        supportedFieldTypes.includes("email") &&
        supportedFieldTypes.includes("phoneNumber")
      ) {
        return "Email or mobile phone";
      }
    }
    return "Email";
  };

  const { currentRetailer: retailer } = useCheckoutRetailer();

  if (!checkoutIntent) {
    return null;
  }

  return (
    <AuthSignUpPageContainer>
      {!retailer ? (
        <div className="page_title">
          <Loading dot />
        </div>
      ) : (
        <>
          <CheckoutInputV2
            label={getUserNameFieldHeading(retailer)}
            inputStyle={{ width: "100%" }}
            style={{ marginTop: ROW_SPACING }}
            onChange={(e) => {
              setUsername(e.target.value as any);
            }}
            sx={{ maxWidth: "100%" }}
            disabled={submitting}
            value={username}
            InputProps={{
              autoComplete: "off",
            }}
          />
          <CheckoutInputV2
            label="Password"
            type="password"
            inputStyle={{ width: "100%" }}
            style={{ marginTop: ROW_SPACING }}
            onChange={(e) => {
              setPassword(e.target.value as any);
            }}
            sx={{ maxWidth: "100%" }}
            disabled={submitting}
            value={password}
            InputProps={{
              autoComplete: "new-password",
              name: "password-checkout-" + Math.random(),
            }}
          />
          <AuthSolidButton
            style={{
              marginTop: ROW_SPACING + 5,
            }}
            disabled={submitting || !username || !password || !edited}
            onClick={async () => {
              await onSaveCredentials(username, password ?? "");
            }}
          >
            Save
          </AuthSolidButton>
          <div style={{ marginTop: ROW_SPACING, width: "100%" }}>
            <AccountConnectBenefits retailerId={retailer.retailerId} />
          </div>
        </>
      )}
    </AuthSignUpPageContainer>
  );
};
